/**
 * WorkspaceContextPill — always-visible workspace switcher in the top header.
 *
 * Solves the post-remodel "which workspace am I in?" problem by surfacing the
 * active workspace name + a color-coded initial chip on the left side of
 * every authenticated page. Clicking opens a dropdown with all workspaces
 * (with recent-first ordering), the workspace creator option, and a deep
 * link to the full Workspaces manager.
 *
 * Bonus polish included:
 *   1. Color is deterministically derived from `workspace_id` (consistent
 *      across sessions, so users learn to recognise "purple = personal LLC,
 *      teal = Smitty CPA").
 *   2. ⌘W keyboard shortcut opens the menu.
 *   3. 2px color-stripe under the header echoes the active workspace's
 *      color, providing a peripheral "you are here" signal that doesn't
 *      need to be read.
 *   4. Recently-used workspaces float to the top of the dropdown after the
 *      active one.
 *
 * Mobile collapses to just the colored initial badge (24px). The existing
 * `mobile-workspace-switcher` testid is preserved so existing automation
 * keeps working.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Home, ChevronDown, Plus, Check,
  Settings as SettingsIcon, Smile, Upload, X, Trash2, Loader2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { apiFetch } from '../fetchInterceptor';
import { cn } from '../lib/utils';

// Deterministic-but-pleasing palette. We pick from a curated set of brand-
// adjacent hues so every workspace badge feels intentional (no random clown
// colors). The mod over workspace_id's hash gives the same workspace the
// same color across sessions.
const PALETTE = [
  { name: 'emerald', bg: 'bg-emerald-500', soft: 'rgba(16,185,129,0.85)', text: 'text-white', stripe: 'rgb(16 185 129)' },
  { name: 'sky',     bg: 'bg-sky-500',     soft: 'rgba(14,165,233,0.85)', text: 'text-white', stripe: 'rgb(14 165 233)' },
  { name: 'violet',  bg: 'bg-violet-500',  soft: 'rgba(139,92,246,0.85)', text: 'text-white', stripe: 'rgb(139 92 246)' },
  { name: 'amber',   bg: 'bg-amber-500',   soft: 'rgba(245,158,11,0.85)', text: 'text-black', stripe: 'rgb(245 158 11)' },
  { name: 'rose',    bg: 'bg-rose-500',    soft: 'rgba(244,63,94,0.85)',  text: 'text-white', stripe: 'rgb(244 63 94)'  },
  { name: 'teal',    bg: 'bg-teal-500',    soft: 'rgba(20,184,166,0.85)', text: 'text-white', stripe: 'rgb(20 184 166)' },
  { name: 'fuchsia', bg: 'bg-fuchsia-500', soft: 'rgba(217,70,239,0.85)', text: 'text-white', stripe: 'rgb(217 70 239)' },
  { name: 'indigo',  bg: 'bg-indigo-500',  soft: 'rgba(99,102,241,0.85)', text: 'text-white', stripe: 'rgb(99 102 241)' },
];

function hashString(s) {
  let h = 0;
  for (let i = 0; i < (s?.length || 0); i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export function getWorkspaceColor(workspaceId) {
  if (!workspaceId) return PALETTE[0];
  return PALETTE[hashString(workspaceId) % PALETTE.length];
}

function getInitials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).slice(0, 2);
  return words.map((w) => w[0]?.toUpperCase() || '').join('') || '?';
}

/**
 * Render the workspace icon — priority is logo URL > emoji > color+initials.
 * Keeps the badge sizing/border identical across all three modes so swapping
 * between them doesn't cause layout shift.
 */
export function WorkspaceIconBadge({ workspace, size = 28, className = '' }) {
  const color = getWorkspaceColor(workspace?.workspace_id);
  const px = `${size}px`;
  if (workspace?.icon_logo_url) {
    return (
      <span
        className={cn('rounded-full overflow-hidden flex-shrink-0 ring-1 ring-black/5', className)}
        style={{ width: px, height: px, backgroundColor: '#fff' }}
        data-testid="workspace-icon-badge-logo"
      >
        <img src={workspace.icon_logo_url} alt={workspace.name || 'logo'}
             className="w-full h-full object-cover" />
      </span>
    );
  }
  if (workspace?.icon_emoji) {
    return (
      <span
        className={cn('rounded-full flex items-center justify-center flex-shrink-0 bg-muted', className)}
        style={{ width: px, height: px, fontSize: `${Math.floor(size * 0.55)}px`, lineHeight: 1 }}
        data-testid="workspace-icon-badge-emoji"
        aria-hidden
      >
        {workspace.icon_emoji}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'rounded-full flex items-center justify-center flex-shrink-0 font-bold',
        color.bg, color.text, className,
      )}
      style={{ width: px, height: px, fontSize: `${Math.floor(size * 0.4)}px` }}
      data-testid="workspace-icon-badge-initials"
      aria-hidden
    >
      {getInitials(workspace?.name)}
    </span>
  );
}

const RECENT_KEY = 'cb_recent_workspaces';

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean).slice(0, 5) : [];
  } catch {
    return [];
  }
}

function pushRecent(workspaceId) {
  try {
    const current = loadRecent().filter((id) => id !== workspaceId);
    current.unshift(workspaceId);
    localStorage.setItem(RECENT_KEY, JSON.stringify(current.slice(0, 5)));
  } catch {
    // localStorage may be unavailable in private/iframe — fail silent.
  }
}

export default function WorkspaceContextPill({
  currentWorkspace,
  workspaces = [],
  selectWorkspace,
  onCreateWorkspace,
  onIconUpdated,
}) {
  const [open, setOpen] = useState(false);
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const navigate = useNavigate();
  const triggerRef = useRef(null);

  // Track recent — push every time the active workspace changes.
  useEffect(() => {
    if (currentWorkspace?.workspace_id) {
      pushRecent(currentWorkspace.workspace_id);
    }
  }, [currentWorkspace?.workspace_id]);

  // ⌘W / Ctrl+W to open. We do NOT intercept when the user holds shift
  // (so existing close-window shortcut on macOS still works).
  useEffect(() => {
    const handler = (e) => {
      const isCmdW = (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'w';
      if (!isCmdW) return;
      // Avoid hijacking the browser's close-tab combo unconditionally — only
      // intercept when no input/textarea has focus AND the user has multiple
      // workspaces. This makes the shortcut feel ambient, not invasive.
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;
      if ((workspaces?.length || 0) < 2) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [workspaces?.length]);

  const activeColor = useMemo(
    () => getWorkspaceColor(currentWorkspace?.workspace_id),
    [currentWorkspace?.workspace_id]
  );

  // Order: active first → recent next → everyone else, deduped.
  const orderedWorkspaces = useMemo(() => {
    if (!workspaces?.length) return [];
    const recent = loadRecent();
    const byId = new Map(workspaces.map((w) => [w.workspace_id, w]));
    const seen = new Set();
    const out = [];
    if (currentWorkspace?.workspace_id && byId.has(currentWorkspace.workspace_id)) {
      out.push(byId.get(currentWorkspace.workspace_id));
      seen.add(currentWorkspace.workspace_id);
    }
    recent.forEach((id) => {
      if (!seen.has(id) && byId.has(id)) {
        out.push(byId.get(id));
        seen.add(id);
      }
    });
    workspaces.forEach((w) => {
      if (!seen.has(w.workspace_id)) {
        out.push(w);
        seen.add(w.workspace_id);
      }
    });
    return out;
  }, [workspaces, currentWorkspace?.workspace_id]);

  if (!currentWorkspace || !workspaces?.length) {
    return null;
  }

  const hasMultiple = workspaces.length > 1;

  return (
    <>
      {/* 2px workspace-color accent stripe under the header. Purely cosmetic
          — uses inline backgroundColor so it always picks up the right hue
          regardless of Tailwind tree-shaking. */}
      <div
        aria-hidden
        className="absolute left-0 right-0 bottom-0 h-[2px] pointer-events-none"
        style={{ backgroundColor: activeColor.stripe, opacity: 0.55 }}
        data-testid="workspace-color-stripe"
      />

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            className={cn(
              'group flex items-center gap-2 h-9 pl-1 pr-2.5 rounded-full',
              'border border-emerald-200/60 dark:border-emerald-500/25 midnight:border-emerald-400/30',
              'bg-white/80 dark:bg-zinc-900/60 midnight:bg-card hover:bg-emerald-50 dark:hover:bg-emerald-950/30',
              'transition-colors shadow-sm hover:shadow-md',
              'max-w-[220px]',
            )}
            data-testid="workspace-context-pill"
            aria-label={`Active workspace: ${currentWorkspace.name}. Click to switch.`}
            title={`${currentWorkspace.name} — press ⌘W to switch${hasMultiple ? '' : ' (only this workspace)'}`}
          >
            <WorkspaceIconBadge workspace={currentWorkspace} size={28} />
            <span className="hidden sm:flex flex-col min-w-0 leading-tight text-left">
              <span className="text-[13px] font-semibold text-foreground truncate">
                {currentWorkspace.name}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                {currentWorkspace.workspace_type === 'personal' ? 'Personal' : 'Business'}
              </span>
            </span>
            {hasMultiple && (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-widest font-mono">
            Workspaces · ⌘W to toggle
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {orderedWorkspaces.map((ws) => {
            const isActive = ws.workspace_id === currentWorkspace.workspace_id;
            return (
              <DropdownMenuItem
                key={ws.workspace_id}
                onClick={() => {
                  selectWorkspace(ws);
                  pushRecent(ws.workspace_id);
                  setOpen(false);
                }}
                className={cn(
                  'cursor-pointer gap-2 py-2',
                  isActive && 'bg-emerald-50 dark:bg-emerald-950/30 midnight:bg-emerald-500/15',
                )}
                data-testid={`workspace-pill-item-${ws.workspace_id}`}
              >
                <WorkspaceIconBadge workspace={ws} size={28} />
                <span className="flex flex-col min-w-0 flex-1 leading-tight">
                  <span className="text-sm font-medium truncate">{ws.name}</span>
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    {ws.workspace_type === 'personal' ? (
                      <Home className="w-2.5 h-2.5" />
                    ) : (
                      <Building2 className="w-2.5 h-2.5" />
                    )}
                    {ws.workspace_type === 'personal' ? 'Personal' : 'Business'}
                    {ws.user_role && <span className="opacity-70">· {ws.user_role}</span>}
                  </span>
                </span>
                {isActive && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setOpen(false);
              setIconDialogOpen(true);
            }}
            className="cursor-pointer"
            data-testid="workspace-pill-customize-icon"
          >
            <Smile className="w-4 h-4 mr-2" />
            Customize {currentWorkspace.name}'s icon
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setOpen(false);
              if (onCreateWorkspace) onCreateWorkspace();
            }}
            className="cursor-pointer"
            data-testid="workspace-pill-new"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Workspace
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              setOpen(false);
              navigate('/workspaces');
            }}
            className="cursor-pointer text-muted-foreground"
            data-testid="workspace-pill-manage"
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Manage Workspaces
          </DropdownMenuItem>
          <DropdownMenuItem disabled className="opacity-60 cursor-default">
            <Badge variant="outline" className="text-[9px] font-mono ml-auto">
              {workspaces.length} total
            </Badge>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <WorkspaceIconDialog
        open={iconDialogOpen}
        onOpenChange={setIconDialogOpen}
        workspace={currentWorkspace}
        onSaved={onIconUpdated}
      />
    </>
  );
}

/**
 * Dialog for customizing a workspace's visual identity.
 * Three modes (mutually exclusive — picking one clears the others):
 *   1. Default color + initials (the auto-generated chip)
 *   2. Emoji (any unicode emoji, max 32 chars to support ZWJ sequences)
 *   3. Uploaded logo (PNG/JPEG/WebP, server-side resized to 256x256 and
 *      stored as a base64 data URI directly on the workspace doc)
 *
 * The currently active mode is highlighted with an emerald ring; "Save" is
 * disabled until the user makes a change. Quick-pick row of common emoji
 * covers the 90% case without forcing the user to find an OS emoji picker.
 */
function WorkspaceIconDialog({ open, onOpenChange, workspace, onSaved }) {
  const [emoji, setEmoji] = useState('');
  const [savingEmoji, setSavingEmoji] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [clearing, setClearing] = useState(false);
  const fileInputRef = useRef(null);

  // Reset local state whenever the dialog (re-)opens.
  useEffect(() => {
    if (open) setEmoji(workspace?.icon_emoji || '');
  }, [open, workspace?.icon_emoji]);

  const QUICK_EMOJI = ['🏢', '🏠', '🏦', '💼', '📊', '🛠️', '🌱', '⚡', '🎯', '🚀', '🐦', '🐾', '🎨', '🍔', '☕', '🛒'];

  const handleSaveEmoji = async (nextEmoji) => {
    setSavingEmoji(true);
    try {
      const res = await apiFetch(`/api/workspaces/${workspace.workspace_id}/icon`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: nextEmoji || null }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed to set emoji');
      toast.success(nextEmoji ? `${workspace.name} now shows ${nextEmoji}` : 'Emoji cleared');
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to set emoji');
    } finally {
      setSavingEmoji(false);
    }
  };

  const handleUploadLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Logo must be 4 MB or smaller');
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch(`/api/workspaces/${workspace.workspace_id}/icon/logo`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Logo upload failed');
      toast.success(`${workspace.name} now uses your custom logo`);
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const res = await apiFetch(`/api/workspaces/${workspace.workspace_id}/icon`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to clear icon');
      toast.success('Reverted to default color + initials');
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClearing(false);
    }
  };

  if (!workspace) return null;
  const hasCustom = !!(workspace.icon_emoji || workspace.icon_logo_url);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="workspace-icon-dialog">
        <DialogHeader>
          <DialogTitle>Customize workspace icon</DialogTitle>
          <DialogDescription>
            Use an emoji or upload a logo to replace the default color + initials chip for{' '}
            <span className="font-semibold text-foreground">{workspace.name}</span>.
          </DialogDescription>
        </DialogHeader>

        {/* Live preview */}
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
          <WorkspaceIconBadge
            workspace={{
              ...workspace,
              icon_emoji: emoji || null,
              icon_logo_url: emoji ? null : workspace.icon_logo_url,
            }}
            size={44}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{workspace.name}</p>
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-mono">Preview</p>
          </div>
        </div>

        {/* Emoji picker */}
        <div className="space-y-2">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Pick an emoji</p>
          <div className="grid grid-cols-8 gap-1.5">
            {QUICK_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEmoji(e)}
                className={cn(
                  'aspect-square rounded-md border flex items-center justify-center text-xl hover:bg-muted transition-colors',
                  emoji === e && 'ring-2 ring-emerald-500 border-emerald-500',
                )}
                data-testid={`emoji-quick-${e}`}
                aria-label={`Use ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Input
              value={emoji}
              onChange={(ev) => setEmoji(ev.target.value)}
              placeholder="Or paste any emoji…"
              maxLength={32}
              className="font-mono"
              data-testid="emoji-custom-input"
            />
            <Button
              onClick={() => handleSaveEmoji(emoji)}
              disabled={savingEmoji || !emoji || emoji === (workspace.icon_emoji || '')}
              data-testid="save-emoji-btn"
              className="gap-1.5"
            >
              {savingEmoji ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save emoji
            </Button>
          </div>
        </div>

        {/* Logo upload */}
        <div className="space-y-2 pt-2">
          <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Or upload a logo</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(ev) => handleUploadLogo(ev.target.files?.[0])}
            data-testid="logo-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingLogo}
            className="w-full gap-2"
            data-testid="upload-logo-btn"
          >
            {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploadingLogo ? 'Uploading…' : 'Choose image (PNG / JPEG / WebP, ≤4 MB)'}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            We'll center-crop and resize to a 256×256 PNG. Stored on your workspace, never shared with other companies.
          </p>
        </div>

        <DialogFooter className="flex sm:flex-row sm:justify-between gap-2">
          {hasCustom ? (
            <Button
              variant="ghost"
              onClick={handleClearAll}
              disabled={clearing}
              className="text-destructive hover:text-destructive gap-1.5"
              data-testid="clear-icon-btn"
            >
              {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Reset to default
            </Button>
          ) : <span />}
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-1.5">
            <X className="w-4 h-4" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
